import { Injectable, NgZone, inject } from '@angular/core'
import { Router } from '@angular/router'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { catchError, endWith, map, Observable, of, switchMap, takeUntil, tap } from 'rxjs'

import { DeepAgentActions } from './deep-agent.actions'

import { DeepAgentApi } from '../api/deep-agent.api'

// Helper to run observable emissions inside NgZone
function runInZone<T>(zone: NgZone) {
  return (source: Observable<T>) =>
    new Observable<T>((observer) =>
      source.subscribe({
        next: (value) => zone.run(() => observer.next(value)),
        error: (error) => zone.run(() => observer.error(error)),
        complete: () => zone.run(() => observer.complete()),
      })
    )
}

@Injectable()
export class DeepAgentEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #deepAgentApi = inject(DeepAgentApi)
  #router = inject(Router)
  #ngZone = inject(NgZone)

  getBuildAgentResponse = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(DeepAgentActions.userSendMessage),
        switchMap(({ message, model, thread_id, action_response, feedback, application_id }) =>
          this.#deepAgentApi
            .getBuildResponse({
              query: message,
              model: model ?? null,
              thread_id: thread_id ?? null,
              action_response: action_response ?? null,
              feedback: feedback ?? null,
              application_id: application_id ?? null,
            })
            .pipe(
              runInZone(this.#ngZone), // Ensure emissions run inside Angular zone
              map((response) => DeepAgentActions.getDeepAgentResponseStream({ response })),
              takeUntil(this.#actions$.pipe(ofType(DeepAgentActions.cancelPendingRequest))),
              catchError((error) => of(DeepAgentActions.getDeepAgentResponseFailure({ error }))),
              endWith(DeepAgentActions.getDeepAgentResponseStreamCompleted())
            )
        )
      ),
    { dispatch: true }
  )

  getBuildWorkflowResponse = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(DeepAgentActions.userSendBuildWorkflowMessage),
        switchMap(({ message, model, thread_id, action_response, feedback, application_id, app_version_id }) =>
          this.#deepAgentApi
            .getBuildWorkflowResponse({
              query: message,
              model: model ?? null,
              thread_id: thread_id ?? null,
              action_response: action_response ?? null,
              feedback: feedback ?? null,
              application_id: application_id ?? null,
              app_version_id: app_version_id ?? null,
            })
            .pipe(
              runInZone(this.#ngZone),
              map((response) => DeepAgentActions.getDeepAgentResponseStream({ response })),
              takeUntil(this.#actions$.pipe(ofType(DeepAgentActions.cancelPendingRequest))),
              catchError((error) => of(DeepAgentActions.getDeepAgentResponseFailure({ error }))),
              endWith(DeepAgentActions.getDeepAgentResponseStreamCompleted())
            )
        )
      ),
    { dispatch: true }
  )

  saveDeepAgent = createEffect(() =>
    this.#actions$.pipe(
      ofType(DeepAgentActions.saveDeepAgent),
      switchMap(({ payload }) =>
        this.#deepAgentApi.createDeepAgent(payload).pipe(
          map((agent) => DeepAgentActions.saveDeepAgentSuccess({ agent })),
          catchError((error) =>
            of(DeepAgentActions.saveDeepAgentFailure({ error: error.message || 'Failed to save agent' }))
          )
        )
      )
    )
  )

  saveDeepAgentSuccess = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(DeepAgentActions.saveDeepAgentSuccess),
        tap(({ agent }) => {
          // Navigate to the agent details page - replace 'new' with the agent id in the current URL
          const currentUrl = this.#router.url
          const newUrl = currentUrl.replace(/\/new$/, `/${agent.id}`)
          this.#router.navigateByUrl(newUrl)
        })
      ),
    { dispatch: false }
  )
}
